import {
  CartLineItemForGA4,
  trackGA4AddToCartEvent,
  trackGA4RemoveLineItemFromCart,
} from '@app/pages/cart/utils/analytics';
import { Box, Image, Text } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { AnalyticsPageTypeProperty } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { ImageFloatingLabel } from '../ImageFloatingLabel';
import { ItemQuantity } from './ItemQuantity';
import styles from './LineItem.module.scss';
import { Price } from './Price';
import { RemoveButton } from './RemoveButton';

interface SharedAnalyticsProps {
  workId: string;
  blankItemId: string;
  marketingProductTypeId: string;
  unitPrice: number;
}

export type OnUpdateCallback = (args: {
  lineItemId: string;
  newQuantity: number;
  signal: AbortSignal;
}) => Promise<void>;

export interface LineItemProps extends SharedAnalyticsProps {
  productUrl: string;
  imageUrl: string;
  productType: string;
  productTypeId: string;
  productOptions: string;
  marketingSku: string;
  workTitle: string;
  artistName: string;
  total: number;
  totalShipping: number;
  undiscountedTotal: number;
  currency: string;
  quantity: number;
  itemId: string;
  analyticsPageType: AnalyticsPageTypeProperty;
  editable?: boolean;
  isDigital?: boolean;
  showQuantity?: boolean;
  gaCode?: string | null;
  onLoadingChange?: (isLoading: boolean) => void;
  onUpdate?: OnUpdateCallback;
  updateError?: string;
  narrow?: boolean;
  disableAddButton?: boolean;
}

interface CartLineItemForGA4Props
  extends SharedAnalyticsProps,
    Pick<
      LineItemProps,
      | 'productTypeId'
      | 'productType'
      | 'undiscountedTotal'
      | 'total'
      | 'isDigital'
      | 'currency'
      | 'quantity'
      | 'gaCode'
    > {}

const DEBOUNCE_RATE = 300;

export const messages = defineMessages({
  freeShippingLabelText: {
    defaultMessage: 'Free shipping',
  },
  giftCard: {
    defaultMessage: 'Gift card',
  },
});

const buildCartLineItemForGA4 = ({
  workId,
  productTypeId,
  blankItemId,
  marketingProductTypeId,
  unitPrice,
  productType,
  undiscountedTotal,
  total,
  currency,
  isDigital,
  quantity,
  gaCode,
}: CartLineItemForGA4Props): CartLineItemForGA4 => ({
  work: { id: workId },
  details: {
    productTypeId,
    blankItemId,
    marketingProductTypeId,
    price: { amount: unitPrice, currency },
    type: productType,
  },
  undiscountedTotal: { amount: undiscountedTotal, currency },
  total: { amount: total, currency },
  isDigital,
  quantity,
  gaCode,
});

const getDiff = (newQty: number, oldQty: number) => Math.abs(newQty - oldQty);

export const LineItem = ({
  productUrl,
  imageUrl,
  productType,
  productTypeId,
  productOptions,
  blankItemId,
  marketingProductTypeId,
  marketingSku,
  workTitle,
  workId,
  artistName,
  total,
  totalShipping,
  undiscountedTotal,
  unitPrice,
  quantity,
  gaCode,
  editable,
  isDigital,
  showQuantity,
  itemId,
  analyticsPageType,
  onUpdate = Promise.resolve,
  updateError,
  narrow,
  disableAddButton,
  onLoadingChange,
}: LineItemProps) => {
  const intl = useIntl();
  const [{ currency }] = useUserInfo<UserInfo>();
  const [shownQuantity, setShownQuantity] = useState<number>(quantity);

  // If quantity updates through other means such as multiple tabs, this handles it
  useEffect(() => {
    setShownQuantity(quantity);
  }, [quantity]);

  const abortController = useRef<AbortController>();
  const lastReqAborted = useRef<boolean>(false);
  const removeConfirmBtn = useRef<HTMLButtonElement>(null);
  const lineItemWrapperEl = useRef<HTMLElement>(null);

  const [isShownRemoveConfirmBtn, setIsShownRemoveConfirmBtn] = useState(false);
  const [updateLineItemError, setUpdateLineItemError] = useState<
    string | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setUpdateLineItemError(updateError);
  }, [updateError]);

  const abortLastRequest = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = undefined;
      lastReqAborted.current = true;
    }
  };

  /* The debounce is there to allow the users time to finishing inputting
   * the new quantity before we do a call to the API. We don't want to do
   * an API call on every keystroke or every 'spamming' clicks.
   *
   * This debounce will affect how we write our tests.
   * In Cypress, we have to explicitly set a cy.wait() before we do another
   * interaction which result in updating with the line item's qty.
   * In Jest, we will need to mock the timer with jest.useFakeTimers().
   */
  const debouncedUpdateLineItem = debounce(
    (newQuantity: number, diffBetweenNewAndOldQty: number) => {
      setUpdateLineItemError(undefined);

      /* we still want to show the line item's quantity to the amount
       * that they are inputting regardless of whether we have updated in
       * the line item's quantity in the cart.
       */
      setShownQuantity(newQuantity);

      const controller = new window.AbortController();

      abortController.current = controller;

      if (!lastReqAborted.current) {
        onLoadingChange?.(true);
      }

      const analyticsLineItem = buildCartLineItemForGA4({
        workId,
        productTypeId,
        blankItemId,
        marketingProductTypeId,
        unitPrice,
        productType,
        undiscountedTotal,
        total,
        currency,
        isDigital,
        quantity,
        gaCode,
      });

      if (newQuantity > quantity) {
        trackGA4AddToCartEvent({
          currency,
          quantity: diffBetweenNewAndOldQty,
          cartLineItem: analyticsLineItem,
          pageType: analyticsPageType,
        });
      } else {
        trackGA4RemoveLineItemFromCart({
          currency,
          lineItem: analyticsLineItem,
          quantity: diffBetweenNewAndOldQty,
          pageType: analyticsPageType,
        });
      }

      onUpdate({
        lineItemId: itemId,
        newQuantity,
        signal: controller.signal,
      })
        .catch(() => {
          setShownQuantity(quantity);
        })
        .finally(() => {
          setLoading(false);
          abortController.current = undefined;
          lastReqAborted.current = false;
        });
    },
    DEBOUNCE_RATE
  );

  const toggleRemoveConfirmBtn = (isOpen: boolean) => {
    if (isOpen) {
      setIsShownRemoveConfirmBtn(true);
      if (removeConfirmBtn.current) {
        removeConfirmBtn.current.disabled = false;
        removeConfirmBtn.current.focus({ preventScroll: true });
      }
    } else {
      setIsShownRemoveConfirmBtn(false);
    }
  };

  const handleQuantityChange = (newQty: number) => {
    if (newQty === 0) {
      toggleRemoveConfirmBtn(true);
      return;
    }
    abortLastRequest();
    debouncedUpdateLineItem(newQty, getDiff(newQty, quantity));
  };

  const handleRemoveConfirmClick = () => {
    lineItemWrapperEl?.current?.classList.add(styles.wrapperOut);
    abortLastRequest();
    debouncedUpdateLineItem(0, getDiff(0, quantity));
  };

  const showFreeShippingTag = totalShipping === 0 && !isDigital;
  const isGiftCard = isDigital && productUrl.includes('/gift-certificates/');
  const imageAlt =
    !workTitle && isGiftCard
      ? intl.formatMessage(messages.giftCard)
      : workTitle;

  return (
    <Box
      className={[styles.wrapper, narrow && styles.narrow]
        .filter(Boolean)
        .join(' ')}
      data-show-remove-confirmation={isShownRemoveConfirmBtn}
      ref={lineItemWrapperEl}
    >
      <Box className={styles.content} data-testid="line-item-summary">
        <Box className={styles.imageContainer}>
          <a href={productUrl} className={styles.image}>
            <Image size="medium" alt={imageAlt} src={imageUrl} />
          </a>
          {showFreeShippingTag && (
            <Box className={styles.labelContainer}>
              <ImageFloatingLabel
                labelText={intl.formatMessage(messages.freeShippingLabelText)}
              />
            </Box>
          )}
        </Box>
        <Box className={styles.details}>
          <a
            href={productUrl}
            className={`${styles.overflowText} ${styles.link}`}
          >
            <Text
              type="display5"
              element="h5"
              data-testid="line-item-work-title"
            >
              {workTitle}
            </Text>
          </a>
          <Text type="body2" muted data-testid="line-item-product-type">
            {productType}
          </Text>
          <Text
            type="body2"
            muted
            className={styles.overflowText}
            data-testid="line-item-product-options"
          >
            {productOptions}
          </Text>
        </Box>

        <Box className={styles.quantity}>
          {showQuantity && (
            <ItemQuantity
              quantity={shownQuantity}
              editable={editable}
              disabled={isShownRemoveConfirmBtn}
              onQuantityChange={handleQuantityChange}
              error={updateLineItemError}
              disableAddButton={disableAddButton}
            />
          )}
        </Box>
        <Price
          total={total}
          undiscountedTotal={undiscountedTotal}
          currency={currency}
          loading={loading}
        />
        {editable && (
          <>
            <RemoveButton
              onRemoveConfirm={handleRemoveConfirmClick}
              toggleShowConfirm={toggleRemoveConfirmBtn}
              confirmButtonRef={removeConfirmBtn}
              showRemoveConfirm={isShownRemoveConfirmBtn}
            />

            <Box
              className={styles.overlay}
              onClick={() => {
                toggleRemoveConfirmBtn(false);
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
